import icn_pie_chart from "assets/images/icons/icn_pie_chart.svg";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";

import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import FirmwareComplianceChart from "components/FirmwareComplianceChart";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const ActionList = ({ items = [] }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Good — No Displaced Assets
      </Typography>

      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        All monitored assets are connected at their authorized network locations
        and ports.​
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Warning — 1 Displaced Asset​
      </Typography>

      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        One asset appears on an unauthorized port or segment — possibly a
        planned relocation or minor misconfiguration.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions:
      </Typography>

      <ActionList
        items={[
          "· Verify whether the device movement is intentional.​",
          "· Check VLAN assignments and patch-panel cabling.​",
        ]}
      />
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Critical — More Than 1 Displaced Asset​
      </Typography>

      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Multiple assets are displaced or on unauthorized segments, indicating
        configuration errors or potential security risks
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>

      <ActionList
        items={[
          "· Investigate the affected devices and confirm their physical locations.​",
          "· Restore proper network placement and update the asset inventory.​",
        ]}
      />
    </Box>
  </Box>
);

const AbnormalLocationAndAssestDisplacement = ({
  onClick,
  noData = false,
  pieGroupsData = [],
  loading = false,
}) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    return Array.isArray(pieGroupsData) ? pieGroupsData : [];
  }, [pieGroupsData]);

  const totalCount = useMemo(() => {
    return chartData.reduce((sum, item) => {
      return sum + Number(item?.value || 0);
    }, 0);
  }, [chartData]);

  const hasData = chartData.length > 0;

  const percentages = useMemo(() => {
    const getValue = (name) => {
      return Number(chartData.find((item) => item.name === name)?.value || 0);
    };

    if (!totalCount) {
      return {
        secured: 0,
        learning: 0,
        displaced: 0,
      };
    }

    return {
      secured: Math.round((getValue("Secured") / totalCount) * 100),
      learning: Math.round((getValue("Learning") / totalCount) * 100),
      displaced: Math.round((getValue("Displaced") / totalCount) * 100),
    };
  }, [chartData, totalCount]);

  const totalValue = String(totalCount);

  const getPercent = (name) => {
    if (name === "Secured") return `${percentages.secured}%`;
    if (name === "Learning") return `${percentages.learning}%`;
    if (name === "Displaced") return `${percentages.displaced}%`;

    return "0%";
  };

  const legendFormatter = (name) => {
    return `{name|${name}} {percent|${getPercent(name)}}`;
  };

  const displacedItem = chartData.find((item) => item.name === "Displaced");

  const displacedCount = Number(displacedItem?.value || 0);

  const status =
    displacedCount > 1 ? "Critical" : displacedCount > 0 ? "Warning" : "Good";

  const statusColor =
    status === "Critical"
      ? theme.palette.primary.redPrimary
      : status === "Warning"
        ? theme.palette.primary.orangeThird
        : theme.palette.primary.greenPrimary;

  return (
    <Box
      onClick={noData ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !noData ? "pointer" : "default",
        "&:hover":
          onClick && !noData
            ? {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }
            : {},
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box
          component="img"
          src={icn_pie_chart}
          sx={{ width: "18px", height: "18px", mr: 1 }}
        />

        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[1],
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
            mr: 1,
          }}
        >
          Abnormal Location & Asset Displacement
        </Typography>

        <HtmlTooltip title={<TooltipContent />} placement="auto">
          <Box
            component="img"
            src={icn_info_solid}
            sx={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
        </HtmlTooltip>
      </Box>

      {loading ? (
        <Box
          sx={{
            minHeight: "320px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : noData || !hasData ? (
        <Box
          sx={{
            minHeight: "320px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "flex-end" },
              px: 2,
              pt: 1,
              pb: 2,
              mb: 2,
              borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box sx={{ px: 0, pt: 0, pb: 1 }}>
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                }}
              >
                Status
              </Typography>

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[6],
                  lineHeight: 1.09,
                  fontWeight: theme.typography.customFontWeight[3],
                  color: statusColor,
                }}
              >
                {status}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: 1,
              pt: 1,
            }}
          >
            <Box
              sx={{
                width: { xs: 520, md: 560 },
                height: { xs: 240, md: 260 },
                maxWidth: "100%",
              }}
            >
              <FirmwareComplianceChart
                data={chartData}
                height="260px"
                totalLabel="Total"
                totalValue={totalValue}
                legendFormatter={legendFormatter}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AbnormalLocationAndAssestDisplacement;
